import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, User, Bot, Database, Shield, AlertTriangle, Sparkles } from 'lucide-react';
import { useGraphStore } from '../store/graphStore';
import { Badge } from '../../../ui/common-ui/badge';
import { Input } from '../../../ui/common-ui/input';

const typeIcons: Record<string, React.ElementType> = {
  user: User,
  service_account: Bot,
  resource: Database,
  role: Shield,
  anomaly: AlertTriangle,
};

const typeColors: Record<string, string> = {
  user: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  service_account: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  resource: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  role: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  anomaly: 'bg-red-500/10 text-red-500 border-red-500/20',
};

export function CollapsibleSearchBar() {
  const {
    allNodes,
    searchQuery,
    searchResults,
    isSearchFocused,
    activeConnectionType,
    setSearchQuery,
    setSearchFocused,
    clearSearch,
    navigateToNode,
    zoomToHighlightedNodes,
  } = useGraphStore();

  const [localQuery, setLocalQuery] = useState(searchQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  // Debounced search with zoom to highlighted nodes
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setSearchQuery(localQuery);
      if (localQuery.trim()) {
        zoomToHighlightedNodes();
      }
    }, 150);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [localQuery, setSearchQuery, zoomToHighlightedNodes]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setSearchFocused]);

  // Get matched nodes with details - deduplicated by ID
  const matchedNodes = useMemo(() => {
    if (!searchResults.length) return [];

    const seenIds = new Set<string>();
    const uniqueNodes: typeof allNodes = [];

    searchResults.forEach(id => {
      if (seenIds.has(id)) return;
      const node = allNodes.find(n => n.id === id);
      if (node) {
        seenIds.add(id);
        uniqueNodes.push(node);
      }
    });

    return uniqueNodes.slice(0, 8);
  }, [searchResults, allNodes]);

  // Group results by type - deduplicated
  const groupedResults = useMemo(() => {
    const groups: Record<string, typeof matchedNodes> = {};
    const seenLabels = new Set<string>();

    matchedNodes.forEach(node => {
      if (!node) return;
      const type = node.type;
      const uniqueKey = `${node.label}-${node.props?.email || ''}`;
      if (seenLabels.has(uniqueKey)) return;
      seenLabels.add(uniqueKey);

      if (!groups[type]) groups[type] = [];
      groups[type].push(node);
    });

    return groups;
  }, [matchedNodes]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalQuery(e.target.value);
  };

  const handleClear = () => {
    setLocalQuery('');
    clearSearch();
    setSearchFocused(false);
    inputRef.current?.blur();
  };

  const handleSelectNode = (nodeId: string) => {
    navigateToNode(nodeId);
    setLocalQuery('');
    clearSearch();
    setSearchFocused(false);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'user': return 'Users';
      case 'service_account': return 'Service Accounts';
      case 'resource': return 'Resources';
      case 'role': return 'Roles';
      case 'anomaly': return 'Anomalies';
      default: return type;
    }
  };

  const hasResults = searchResults.length > 0;
  const showDropdown = isSearchFocused && localQuery.trim().length > 0;

  return (
    <div
      className="relative"
    >
      {/* Always expanded search bar */}
      <div className="w-[500px] md:w-[600px] lg:w-[700px]">
        {/* Search Input */}
        <div className="glass-panel rounded-2xl shadow-lg">
          <div className="relative flex items-center px-5 py-3">
            <div className={`relative transition-transform duration-300 ${isSearchFocused ? 'scale-110' : ''}`}>
              <Search className={`w-5 h-5 transition-colors duration-300 ${isSearchFocused ? 'text-primary' : 'text-muted-foreground'
                }`} />
            </div>

            <Input
              ref={inputRef}
              placeholder={
                activeConnectionType === 'users' ? 'Search users...' :
                  activeConnectionType === 'sa' ? 'Search service accounts...' :
                    'Search resources...'
              }
              className="pl-3 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-base flex-1 shadow-none"
              value={localQuery}
              onChange={handleInputChange}
              onFocus={() => setSearchFocused(true)}
            />

            {localQuery && hasResults && (
              <Badge
                variant="secondary"
                className="mr-2 text-xs font-medium bg-primary/10 text-primary border-0 animate-fade-in"
              >
                {searchResults.length} found
              </Badge>
            )}

            {localQuery && (
              <button
                onClick={handleClear}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Dropdown Results */}
        {showDropdown && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 right-0 mt-2 glass-panel rounded-xl shadow-xl border border-border overflow-hidden z-[80] animate-fade-in"
          >
            {hasResults ? (
              <div className="max-h-80 overflow-y-auto">
                {Object.entries(groupedResults).map(([type, nodes]) => (
                  <div key={type}>
                    <div className="px-4 py-2 bg-muted/50 border-b border-border/50 sticky top-0">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {getTypeLabel(type)}
                      </span>
                    </div>

                    {nodes.map((node) => {
                      if (!node) return null;
                      const Icon = typeIcons[node.type] || Sparkles;
                      const colorClass = typeColors[node.type] || 'bg-muted text-muted-foreground';

                      return (
                        <button
                          key={node.id}
                          onClick={() => handleSelectNode(node.id)}
                          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left group"
                        >
                          <div className={`p-2 rounded-lg border ${colorClass} transition-transform group-hover:scale-110`}>
                            <Icon className="w-4 h-4" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">
                              {highlightMatch(node.label, localQuery)}
                            </div>
                            {node.props?.email && (
                              <div className="text-xs text-muted-foreground truncate">
                                {highlightMatch(node.props.email, localQuery)}
                              </div>
                            )}
                          </div>

                          <Badge variant="outline" className="text-xs capitalize shrink-0">
                            {node.type.replace('_', ' ')}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center">
                <Search className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No results for "{localQuery}"</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Try searching for users, resources, or roles</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper to highlight matching text
function highlightMatch(text: string, query: string) {
  if (!query.trim()) return text;

  const parts = text.split(new RegExp(`(${escapeRegex(query)})`, 'gi'));

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="bg-primary/20 text-primary font-semibold rounded px-0.5">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function escapeRegex(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
